import { Button, FieldBlock, Input, Modal, Select, Textarea, useToast } from '@orbind/ui'
import { useEffect, useState } from 'react'
import { IconField } from '../../components/IconPicker.tsx'
import { buscarCredenciais, buscarFaviconDoSite, useCreateApp, useUpdateApp, type UserApp } from './data.ts'

/**
 * Cadastro e edicao de aplicacao.
 *
 * A senha e tratada a parte do resto do formulario: ao editar, ela chega vazia
 * e so viaja de volta se o usuario digitar algo — assim salvar o titulo nao
 * apaga a credencial por omissao. Para remover de propósito existe o botao
 * "Esquecer senha".
 */
export function AppFormModal({
  open,
  onClose,
  editando,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  /** `null` = cadastro novo. */
  editando: UserApp | null
  onSaved: (app: UserApp) => void
}) {
  const toast = useToast()
  const criar = useCreateApp()
  const atualizar = useUpdateApp()

  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [description, setDescription] = useState('')
  const [icon, setIcon] = useState('')
  const [iconImageId, setIconImageId] = useState<string | null>(null)
  const [openMode, setOpenMode] = useState<'embedded' | 'tab'>('embedded')
  const [abbreviation, setAbbreviation] = useState('')
  const [buscandoIcone, setBuscandoIcone] = useState(false)
  const [zoom, setZoom] = useState(100)
  const [loginUsername, setLoginUsername] = useState('')
  const [senha, setSenha] = useState('')
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [apagarSenha, setApagarSenha] = useState(false)
  const [senhaCarregada, setSenhaCarregada] = useState(false)

  useEffect(() => {
    if (!open) return
    setTitle(editando?.title ?? '')
    setUrl(editando?.url ?? '')
    setDescription(editando?.description ?? '')
    setIcon(editando?.icon ?? 'fas fa-globe')
    setIconImageId(editando?.iconImageId ?? null)
    setOpenMode(editando?.openMode ?? 'embedded')
    setAbbreviation(editando?.abbreviation ?? '')
    setZoom(editando?.zoom ?? 100)
    setLoginUsername(editando?.loginUsername ?? '')
    setSenha('')
    setMostrarSenha(false)
    setApagarSenha(false)
    setSenhaCarregada(false)
  }, [open, editando])

  /** Traz a senha guardada só quando o usuário pede para vê-la. */
  const revelarSenha = async () => {
    if (senhaCarregada || !editando?.hasCredentials) {
      setMostrarSenha((v) => !v)
      return
    }
    try {
      const credenciais = await buscarCredenciais(editando.id)
      setSenha(credenciais.password ?? '')
      setSenhaCarregada(true)
      setMostrarSenha(true)
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível ler a senha.' })
    }
  }

  /** Pega o favicon do site e usa como logo. `silencioso` = auto no blur. */
  const usarFaviconDoSite = async (silencioso = false) => {
    const alvo = url.trim()
    if (alvo === '') return
    const endereco = /^https?:\/\//i.test(alvo) ? alvo : `https://${alvo}`
    setBuscandoIcone(true)
    try {
      const iid = await buscarFaviconDoSite(endereco)
      setIconImageId(iid)
      setIcon('')
    } catch (erro) {
      if (!silencioso) {
        toast.push({ tone: 'warning', message: erro instanceof Error ? erro.message : 'Não encontrei o ícone do site.' })
      }
    } finally {
      setBuscandoIcone(false)
    }
  }

  const salvando = criar.isPending || atualizar.isPending
  const podeSalvar = title.trim() !== '' && url.trim() !== '' && !salvando

  const salvar = async () => {
    if (!podeSalvar) return
    // URL sem esquema é o erro mais comum de digitação — completar é melhor
    // que devolver "endereço inválido" para quem escreveu o endereço certo
    const enderecoLimpo = url.trim()
    const endereco = /^https?:\/\//i.test(enderecoLimpo) ? enderecoLimpo : `https://${enderecoLimpo}`

    const base = {
      title: title.trim(),
      url: endereco,
      description: description.trim() === '' ? null : description.trim(),
      icon: iconImageId ? null : icon,
      iconImageId,
      openMode,
      abbreviation: abbreviation.trim() === '' ? null : abbreviation.trim().toUpperCase(),
      zoom,
      loginUsername: loginUsername.trim() === '' ? null : loginUsername.trim(),
      // ausente = mantém o que está guardado
      ...(apagarSenha ? { loginPassword: null } : senha !== '' ? { loginPassword: senha } : {}),
    }

    try {
      const salva = editando
        ? await atualizar.mutateAsync({ id: editando.id, ...base })
        : await criar.mutateAsync(base)
      toast.push({ tone: 'success', message: editando ? 'Aplicação atualizada.' : `${salva.title} adicionada.` })
      onSaved(salva)
      onClose()
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível salvar.' })
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editando ? 'Editar aplicação' : 'Nova aplicação'}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!podeSalvar}
            title={podeSalvar ? undefined : 'Informe o título e o endereço'}
            onClick={() => void salvar()}
          >
            {salvando ? 'Salvando…' : editando ? 'Salvar' : 'Adicionar'}
          </Button>
        </>
      }
    >
      <div className="ob-stack">
        <FieldBlock label="Título" required>
          {({ id }) => (
            <Input
              id={id}
              autoFocus
              placeholder="Como você chama essa aplicação"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          )}
        </FieldBlock>

        <FieldBlock label="Endereço" required hint="O endereço que você abriria no navegador.">
          {({ id }) => (
            <Input
              id={id}
              placeholder="https://…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              // ao sair do campo, se ainda nao ha logo, pega o favicon do site
              onBlur={() => {
                if (!iconImageId && url.trim() !== '') void usarFaviconDoSite(true)
              }}
              inputMode="url"
            />
          )}
        </FieldBlock>

        <div className="appform__grid">
          <div className="appform__iconwrap">
            <IconField
              label="Ícone ou logo"
              value={icon}
              imageId={iconImageId}
              onChange={(escolha) => {
                setIcon(escolha.icon)
                setIconImageId(escolha.iconImageId)
              }}
            />
            <Button
              variant="ghost"
              icon="fas fa-image"
              loading={buscandoIcone}
              disabled={url.trim() === ''}
              onClick={() => void usarFaviconDoSite(false)}
            >
              Ícone do site
            </Button>
          </div>

          <FieldBlock label="Abrir" hint="Sites que recusam ser embutidos precisam de nova aba.">
            {() => (
              <Select
                value={openMode}
                onChange={(v) => setOpenMode(v as 'embedded' | 'tab')}
                options={[
                  { value: 'embedded', label: 'Dentro do Orbind' },
                  { value: 'tab', label: 'Em nova aba' },
                ]}
              />
            )}
          </FieldBlock>

          <FieldBlock label="Zoom" hint="Zoom com que a aplicação abre no desktop (100% é o normal).">
            {() => (
              <Select
                value={String(zoom)}
                onChange={(v) => setZoom(Number(v))}
                options={[50, 67, 75, 80, 90, 100, 110, 125, 150, 175, 200].map((z) => ({
                  value: String(z),
                  label: `${z}%`,
                }))}
              />
            )}
          </FieldBlock>

          <FieldBlock label="Sigla" hint="Aparece na barra quando não há imagem. Vazia = tirada do título.">
            {({ id }) => (
              <Input
                id={id}
                maxLength={6}
                placeholder="Ex.: OB"
                value={abbreviation}
                onChange={(e) => setAbbreviation(e.target.value.toUpperCase())}
                style={{ textTransform: 'uppercase' }}
              />
            )}
          </FieldBlock>
        </div>

        <FieldBlock label="Descrição" hint="Opcional.">
          {({ id }) => (
            <Textarea
              id={id}
              rows={2}
              placeholder="Para que você usa essa aplicação"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </FieldBlock>

        <div className="appform__cofre">
          <div className="appform__cofre-topo">
            <i className="fas fa-key" aria-hidden />
            <span>Acesso</span>
          </div>
          <p className="appform__cofre-aviso">
            A senha é guardada cifrada no servidor e pode ser lida de volta para preencher o login. Guarde aqui apenas o
            que você aceita manter recuperável.
          </p>

          <FieldBlock label="Usuário">
            {({ id }) => (
              <Input
                id={id}
                autoComplete="off"
                placeholder="Login usado nessa aplicação"
                value={loginUsername}
                onChange={(e) => setLoginUsername(e.target.value)}
              />
            )}
          </FieldBlock>

          <FieldBlock
            label="Senha"
            hint={
              editando?.hasCredentials && !senhaCarregada
                ? 'Já existe uma senha guardada — deixe em branco para mantê-la.'
                : undefined
            }
          >
            {({ id }) => (
              <div className="appform__senha">
                <Input
                  id={id}
                  type={mostrarSenha ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder={editando?.hasCredentials && !senhaCarregada ? '••••••••' : 'Senha da aplicação'}
                  value={senha}
                  disabled={apagarSenha}
                  onChange={(e) => {
                    setSenha(e.target.value)
                    setApagarSenha(false)
                  }}
                />
                <button
                  type="button"
                  className="appform__senha-btn"
                  aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  title={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  onClick={() => void revelarSenha()}
                >
                  <i className={mostrarSenha ? 'fas fa-eye-slash' : 'fas fa-eye'} aria-hidden />
                </button>
              </div>
            )}
          </FieldBlock>

          {editando?.hasCredentials ? (
            <label className="appform__esquecer">
              <input
                type="checkbox"
                checked={apagarSenha}
                onChange={(e) => {
                  setApagarSenha(e.target.checked)
                  if (e.target.checked) setSenha('')
                }}
              />
              Esquecer a senha guardada
            </label>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}
